import { useCallback, useEffect, useMemo, useRef } from "react";
import { AppShell } from "./components/AppShell";
import { useMuseChat, type Interjections, type ServerVoiceConfig } from "./hooks/useChat";
import { useChatConfig } from "./hooks/useChatConfig";
import { resolveDisplayedModel, type ModelEntry } from "./shared/chat-config";
import { ModelSwitchConfirmDialog } from "./components/ModelSwitchConfirmDialog";
import { useSession } from "./hooks/useSession";
import { useSettings } from "./hooks/useSettings";
import { useAuth } from "./hooks/useAuth";
import { useVoice } from "./voice/useVoice";
import { TokenGate } from "./components/TokenGate";
import type { SessionType, NewSessionOpts } from "./components/NewSessionDialog";
import type { PendingAttachment } from "./components/AttachmentPreview";
import { resolveCompanionForSessionTarget } from "./brain/persona";
import { useProjects } from "./hooks/useProjects";
import { ContinueFreshContext, type ContinueFreshValue } from "./continue-fresh/ContinueFreshContext";

export function MuseApp() {
  const { hasToken, setToken } = useAuth();

  if (!hasToken) {
    return <TokenGate onSubmit={setToken} />;
  }

  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  const { clearToken } = useAuth();
  const { config, availableModels, currentModel, setModel, setEffort, toggleThinking, setLiveModels } = useChatConfig();
  const {
    sessions,
    archivedSessions,
    activeSession,
    activeSessionId,
    activeCompanionIdentity,
    authFailed,
    sessionError,
    retrySessionInit,
    createSession,
    continueFresh,
    followContinuation,
    deleteSession,
    renameSession,
    switchSession,
    togglePin,
    archiveSession,
    restoreSession,
    assignSessionProject,
    applySessionDocumentUpdate,
    pendingProjectMoveSessionIds,
  } = useSession();

  // useSettings must be called before useMuseChat so handleServerVoiceConfig
  // has access to updatePref when it is passed as the third argument.
  const { prefs, updatePref } = useSettings();

  // Tracks the last voice config the server told us about. Used to distinguish
  // "server just hydrated us" from "user actually changed a setting" in the
  // sendVoiceConfig effect below. Null until the server has sent at least one
  // voice_config (which happens on every init/reconnect).
  const lastServerConfigRef = useRef<{
    autoSpeak: boolean;
    ttsMode: string;
    voiceId: string;
    speed: number;
  } | null>(null);

  // Callback passed into useMuseChat so it can seed our prefs from the server's
  // persisted voice_config on every init message (connect + reconnect).
  // We record the server's truth in lastServerConfigRef BEFORE applying the
  // updatePref calls so the sendVoiceConfig effect below can see that these
  // pref values are server-originated and should not be echoed back.
  const handleServerVoiceConfig = useCallback((vc: ServerVoiceConfig) => {
    lastServerConfigRef.current = {
      autoSpeak: vc.autoSpeak,
      ttsMode: vc.ttsMode,
      voiceId: vc.voiceId,
      speed: vc.speed,
    };
    updatePref("autoSpeak", vc.autoSpeak);
    updatePref("ttsMode", vc.ttsMode);
    updatePref("voiceId", vc.voiceId);
    updatePref("playbackSpeed", vc.speed);
  }, [updatePref]);

  // Option A: sync chatConfig.model in the same React batch as setServerModel.
  // Named callbacks (not inline arrows) for stable references across renders.
  const onSyncChatModel = useCallback(
    (model: string, models: ModelEntry[]) => setModel(model, models),
    [setModel],
  );
  // Cancel revert: restore the last-sent model when the user dismisses the dialog.
  const onRevertChatModel = useCallback(
    (model: string) => setModel(model),
    [setModel],
  );

  const {
    safeguardFlag,
    queuedMessages, cancelQueued, takeBackDraft,
    messages, messageMetrics, messageOrigin, messageOriginSessionLabel, status, isLoading, connected, switching, hasConnected, error,
    activeAgents, serverModel, serverModels, serverCompanions, isReplaying,
    sendMessage, stop,
    sendTTSRequest, sendTTSStop, sendVoiceConfig,
    sendReasoningConfig, showReasoning, readReasoningAloud, onServerReasoningConfigRef,
    sendRawWs, onWritingRoomDocumentUpdatedRef, roomStateRef,
    onServerAudioChunkRef, onTTSStartRef, onTTSEndRef, onTTSErrorRef,
    sttAvailable, sttStreamingAvailable, sendSTTAudio, onSTTResultRef,
    sendSTTStreamStart, sendSTTStreamStop, sendBinaryFrame,
    onSTTStreamSegmentRef, onSTTStreamReadyRef, onSTTStreamDoneRef,
    onSTTStreamDisconnectRef, onSTTStreamErrorRef,
    onReactionRef, onReactionSyncRef,
    pendingReactionsRef, pendingReactionSyncsRef,
    turnPhase, turnStartedAt, subprocessAlive,
    pendingModelSwitch, confirmModelSwitch, cancelModelSwitch,
  } = useMuseChat(
    config,
    activeSessionId,
    handleServerVoiceConfig,
    onSyncChatModel,
    onRevertChatModel,
    // PATH B SLICE 4: the active session's own companion — session.identity is
    // real and server-authoritative (every Session the gateway returns carries
    // it), so this is correct even before serverCompanions has arrived.
    activeCompanionIdentity ?? undefined,
  );

  // ADR-023: waiting messages + take-back, handed to the chat surface as one prop.
  const interjections = useMemo<Interjections>(
    () => ({ queued: queuedMessages, onTakeBack: cancelQueued, takeBackDraft }),
    [queuedMessages, cancelQueued, takeBackDraft],
  );

  const {
    projects,
    loading: projectsLoading,
    error: projectsError,
    createProject,
    renameProject,
    applyProjectUpdate,
  } = useProjects(serverCompanions);

  // The live shell is stricter than generic companion labels: a concrete
  // session id without a server-authoritative identity renders neutrally and
  // cannot visually fall through to another companion. The OSS fallback is
  // retained only for a genuine no-target cold render.
  const activeCompanion = resolveCompanionForSessionTarget(
    serverCompanions,
    activeSessionId,
    activeCompanionIdentity,
  );
  // The picker is a status display, not a wish. `config.model` moves as soon as
  // the user chooses a row so the existing confirmation gate can open; the
  // visible label stays on the server-acknowledged model until `ready` arrives.
  const displayedModel = resolveDisplayedModel(serverModel, availableModels, currentModel);

  // Tracks the last show_reasoning value the server told us about. Used to distinguish
  // "server just hydrated us" from "user actually changed the toggle" in the
  // sendReasoningConfig effect below. Null until the server has sent at least one init.
  const lastServerReasoningRef = useRef<boolean | null>(null);
  // Tracks the last read_reasoning_aloud value from the server.
  const lastServerReadAloudRef = useRef<boolean | null>(null);

  // Always-current ref for prefs.showReasoning so the init callback can read
  // the latest user intent without a stale closure. Updated every render.
  const showReasoningPrefRef = useRef<boolean>(prefs.showReasoning ?? true);
  showReasoningPrefRef.current = prefs.showReasoning ?? true;
  // Always-current ref for prefs.readReasoningAloud.
  const readReasoningAloudPrefRef = useRef<boolean>(prefs.readReasoningAloud ?? false);
  readReasoningAloudPrefRef.current = prefs.readReasoningAloud ?? false;

  // Wire the onServerReasoningConfigRef so useChat can tell App when the server
  // sends show_reasoning (and read_reasoning_aloud) in the init frame.
  //
  // User intent wins: records the server value in the refs, then immediately
  // reconciles — if the user's localStorage pref differs from what the server
  // sent, push the user's value back to the server right away.
  // We do NOT call updatePref here because that would overwrite the user's
  // toggle with the server's (possibly stale) value.
  useEffect(() => {
    onServerReasoningConfigRef.current = (enabled: boolean, serverReadAloud: boolean) => {
      lastServerReasoningRef.current = enabled;
      lastServerReadAloudRef.current = serverReadAloud;
      // Reconcile: user intent wins over server restore.
      const userPref = showReasoningPrefRef.current;
      const userReadAloud = readReasoningAloudPrefRef.current;
      const reasoningMismatch = userPref !== enabled;
      const readAloudMismatch = userReadAloud !== serverReadAloud;
      if (reasoningMismatch || readAloudMismatch) {
        sendReasoningConfig(userPref, userReadAloud);
        lastServerReasoningRef.current = userPref;
        lastServerReadAloudRef.current = userReadAloud;
      }
    };
  }, [onServerReasoningConfigRef, sendReasoningConfig]);

  useEffect(() => {
    if (authFailed || error?.message === 'auth_failed') {
      clearToken();
    }
  }, [authFailed, error, clearToken]);

  // On session switch, reset the live model list.
  // The live list must be null until the new session's init frame arrives — otherwise
  // the previous session's model list judges the new identity's restored model against
  // the wrong list and may flash-reset it.
  //
  // Option A supersedes useServerModelSync: the init handler in useChat.ts now calls
  // onSyncChatModel (→ setModel with msg.models) in the same React batch as setServerModel,
  // and resets lastSentConfigRef so the config_change self-seed fires with the correct
  // chatConfig.model. The per-session guard and retry logic from useServerModelSync are
  // no longer needed — the init handler always has msg.models available for lookup.
  useEffect(() => {
    setLiveModels(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionId, setLiveModels]);

  // Sync the live model registry from the gateway into useChatConfig.
  // Fires after init via useEffect; onSyncChatModel (called synchronously in the
  // init handler) may have already set chatConfig.model using msg.models — this
  // effect just ensures liveModels is populated for subsequent picker renders.
  useEffect(() => {
    if (!serverModels) return;
    setLiveModels(serverModels);
  }, [serverModels, setLiveModels]);

  const voice = useVoice(messages, status ?? "idle", {
    voiceEnabled: prefs.voiceEnabled,
    voiceId: prefs.voiceId,
    autoSpeak: prefs.autoSpeak,
    playbackSpeed: prefs.playbackSpeed,
    ttsMode: prefs.ttsMode,
    sendTTSRequest,
    sendTTSStop,
  });

  // Named (not inlined at the AppShell call site below) so the SAME function
  // identity backs both AppShell's own `onSpeedChange` prop and the
  // read-aloud `writingRoomVoice` bundle just below — one speed handler,
  // shared, matching the brief's "+ onSpeedChange it already passes to
  // AppShell" (WR-READ-ALOUD-JUNE-BRIEF.md).
  const handleSpeedChange = useCallback(
    (speed: number) => updatePref("playbackSpeed", speed),
    [updatePref],
  );

  // Rainer/Thorn (2026-10-10 review, round 3) — the Writing Room's
  // cross-lane collision guard: sends the ws-scoped, non-persisted
  // `reading_lane` message directly (server.ts's readingLaneByWs, NEVER
  // voiceConfigs/transcript.setPreferences — see the sendVoiceConfig
  // reconciliation effect's own doc comment further down for why). Stable
  // identity via useCallback so the writingRoomVoice memo below never
  // needs to rebuild just because this function was recreated.
  const sendReadingLane = useCallback((active: boolean) => {
    sendRawWs({ type: 'reading_lane', active });
  }, [sendRawWs]);

  // Read-aloud slice 1 — the Writing Room's voice bundle, built once from
  // this SAME `voice` (useVoice) instance the individual props below also
  // come from. Memoized so WritingRoomLayer's `voice` prop keeps a stable
  // identity across renders that don't actually change any of these values
  // (voice.audioCache is a stable ref-backed instance; the rest are plain
  // primitives/functions already memoized inside useVoice itself).
  const writingRoomVoice = useMemo(() => ({
    speakText: voice.speakText,
    stopSpeaking: voice.stopSpeaking,
    speakingId: voice.speakingMessageId,
    seekTo: voice.seekTo,
    getPlaybackProgress: voice.getPlaybackProgress,
    audioCache: voice.audioCache,
    voiceId: prefs.voiceId,
    playbackSpeed: voice.playbackSpeed,
    onSpeedChange: handleSpeedChange,
    voiceState: voice.voiceState,
    // Reeve HIGH, PR #68 2nd round — the server TTS engine's own id-scoped
    // failure signal (browser-engine failures still surface via voiceState
    // alone; see WritingRoomVoice's own doc comment on this field).
    erroredId: voice.erroredMessageId,
    // Rainer/Thorn, 2026-10-10 — the generation-terminal signal the chunk
    // queue's advance effect gates on (see WritingRoomVoice's own doc
    // comment on this field).
    ttsEndedId: voice.ttsEndedId,
    // Rainer/Thorn, 2026-10-10 review round 3 — cross-lane collision
    // guard; see WritingRoomVoice's own doc comment on this field, and
    // sendReadingLane's own doc comment above for why it has to flow all
    // the way back up here rather than staying local to the Writing Room.
    suspendAutoSpeak: sendReadingLane,
  }), [
    voice.speakText, voice.stopSpeaking, voice.speakingMessageId, voice.seekTo,
    voice.getPlaybackProgress, voice.audioCache, prefs.voiceId, voice.playbackSpeed,
    handleSpeedChange, voice.voiceState, voice.erroredMessageId, voice.ttsEndedId,
    sendReadingLane,
  ]);

  // Wire useVoice server TTS callbacks into useChat's refs.
  // Wrapped in useEffect to avoid mutating refs during the render phase.
  useEffect(() => {
    onServerAudioChunkRef.current = voice.onServerAudioChunk;
    onTTSStartRef.current = voice.onTTSStart;
    onTTSEndRef.current = voice.onTTSEnd;
    onTTSErrorRef.current = voice.onTTSError;
  }, [
    voice.onServerAudioChunk, voice.onTTSStart, voice.onTTSEnd, voice.onTTSError,
    onServerAudioChunkRef, onTTSStartRef, onTTSEndRef, onTTSErrorRef,
  ]);

  // Sync voice config to the gateway only when the user has actually changed a
  // setting — not when the server just hydrated our prefs via handleServerVoiceConfig.
  //
  // The race this kills:
  //   WS init → server sends voice_config → handleServerVoiceConfig schedules
  //   updatePref calls → connected flips true in the same render cycle → this
  //   effect would fire with STALE prefs (React batches the updatePref updates),
  //   sending autoSpeak: false back to the server and poisoning the DB.
  //
  // The gate: only push when lastServerConfigRef has been populated (server has
  // spoken at least once) AND the current prefs differ from what the server told
  // us (meaning the user actually changed something after the last server sync).
  // After pushing, advance the ref so subsequent renders don't re-push the same
  // values.
  //
  // Rainer/Thorn (2026-10-10 review, round 3) — the Writing Room's
  // cross-lane auto-speak suppression does NOT go through this effect (or
  // `voice_config`/`prefs.autoSpeak` at all): `transcript.setPreferences`
  // persists `voice_config` to the DB, so routing a reading session's
  // temporary suppression through it would mean a tab close, crash,
  // reload, or a phone going to sleep mid-reading permanently and silently
  // flips the user's REAL autoSpeak preference off. See WritingRoomVoice's
  // own `suspendAutoSpeak` doc comment (read-aloud.ts) — it sends a
  // ws-scoped, non-persisted `reading_lane` message directly via
  // `sendRawWs`, below.
  useEffect(() => {
    if (!connected) return;
    const server = lastServerConfigRef.current;
    if (server === null) return; // haven't heard from server yet — don't push defaults
    const speed = prefs.playbackSpeed ?? 1.0;
    const userChanged =
      prefs.autoSpeak !== server.autoSpeak ||
      prefs.ttsMode !== server.ttsMode ||
      prefs.voiceId !== server.voiceId ||
      speed !== server.speed;
    if (!userChanged) return;
    sendVoiceConfig(prefs.autoSpeak, prefs.ttsMode, prefs.voiceId, speed);
    // Advance the ref so the next render doesn't re-push the same values.
    lastServerConfigRef.current = {
      autoSpeak: prefs.autoSpeak,
      ttsMode: prefs.ttsMode,
      voiceId: prefs.voiceId,
      speed,
    };
  }, [connected, prefs.autoSpeak, prefs.ttsMode, prefs.voiceId, prefs.playbackSpeed, sendVoiceConfig]);

  // Sync reasoning toggles to the gateway only when the user has actually changed them —
  // not when the server just hydrated our prefs via onServerReasoningConfigRef.
  useEffect(() => {
    if (!connected) return;
    const server = lastServerReasoningRef.current;
    const serverReadAloud = lastServerReadAloudRef.current;
    if (server === null) return; // haven't heard from server yet — don't push defaults
    const reasoningChanged = prefs.showReasoning !== server;
    const readAloudChanged = serverReadAloud !== null && prefs.readReasoningAloud !== serverReadAloud;
    if (!reasoningChanged && !readAloudChanged) return;
    sendReasoningConfig(prefs.showReasoning, prefs.readReasoningAloud);
    // Advance the refs so the next render doesn't re-push the same values.
    lastServerReasoningRef.current = prefs.showReasoning;
    lastServerReadAloudRef.current = prefs.readReasoningAloud;
  }, [connected, prefs.showReasoning, prefs.readReasoningAloud, sendReasoningConfig]);

  function handleToggleVoiceMode() {
    const entering = !prefs.voiceMode;
    updatePref("voiceMode", entering);
    if (entering) {
      // Enable voice engine but do NOT auto-speak existing messages
      updatePref("voiceEnabled", true);
      if (voice.voiceState === "unloaded") {
        voice.initVoice();
      }
    }
  }

  const sessionType = (activeSession?.session_type as SessionType) ?? null;

  const handleSend = useCallback((text: string, attachments?: PendingAttachment[]) => {
    if (!attachments || attachments.length === 0) {
      sendMessage({ text });
      return;
    }

    // Separate images and documents
    const imageFiles = attachments.filter(a =>
      a.serverResponse?.media_type.startsWith("image/") && a.serverResponse?.url
    );
    const docFiles = attachments.filter(a =>
      a.serverResponse?.extractedText
    );

    // Build message text with document context
    let messageText = text;
    if (docFiles.length > 0) {
      const docContext = docFiles
        .map(a => `---\n[Attached: ${a.serverResponse!.filename}]\n${a.serverResponse!.extractedText}`)
        .join("\n\n");
      messageText = text ? `${text}\n\n${docContext}` : docContext;
    }

    // For images: send as FileUIPart[] for vision-capable models.
    // Data URLs travel through WS → DO SQLite → convertToModelMessages.
    // The DO's 1.8MB row limit can truncate large images — client-side
    // resize (2048px max, JPEG 0.8) keeps them well under this limit.
    const files = imageFiles.map(a => ({
      type: "file" as const,
      mediaType: a.serverResponse!.media_type,
      filename: a.serverResponse!.filename,
      url: a.serverResponse!.url!,
    }));

    if (files.length > 0) {
      sendMessage({ text: messageText || "Please look at the attached image(s).", files });
    } else {
      sendMessage({ text: messageText });
    }
  }, [sendMessage]);

  // Read at click time so the memo does not rebuild on every streamed token.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Continue fresh (ADR-022). The successor is derived from the session list
  // (continued_from_session_id), so the forward card survives reloads.
  const continueFreshValue = useMemo<ContinueFreshValue | null>(() => {
    if (!activeSessionId) return null;
    const next = [...sessions, ...archivedSessions].find((s) => s.continued_from_session_id === activeSessionId);
    return {
      flag: safeguardFlag,
      isLoading,
      successor: next ? { id: next.id, label: next.label } : null,
      onTryAgain: () => {
        const lastUser = [...messagesRef.current].reverse().find((m) => m.role === "user");
        const text = lastUser?.parts
          .filter((p): p is { type: "text"; text: string } => p.type === "text")
          .map((p) => p.text)
          .join("\n");
        if (text) sendMessage({ text });
      },
      onContinueFresh: async () => {
        const outcome = await continueFresh(activeSessionId);
        return outcome.ok ? "ok" : outcome.reason;
      },
      onFollowSuccessor: () => { if (next) followContinuation(activeSessionId, next); },
    };
  }, [activeSessionId, sessions, archivedSessions, safeguardFlag, isLoading, sendMessage, continueFresh, followContinuation]);

  async function handleNewSession(opts: NewSessionOpts) {
    await createSession(opts);
  }

  if (sessionError) {
    return (
      <div style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "1rem",
        background: "var(--bg-void)",
        color: "var(--text-secondary)",
        fontFamily: "var(--font-display)",
        padding: "2rem",
        textAlign: "center",
      }}>
        <p style={{ margin: 0 }}>{sessionError}</p>
        <button
          onClick={retrySessionInit}
          style={{
            padding: "0.5rem 1.25rem",
            background: "var(--accent)",
            color: "var(--bg-void)",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontFamily: "var(--font-display)",
            fontSize: "0.9rem",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Resolve model labels for the confirmation dialog.
  const pendingFromLabel = pendingModelSwitch
    ? (availableModels.find(m => m.id === pendingModelSwitch.from)?.label ?? pendingModelSwitch.from)
    : "";
  const pendingToLabel = pendingModelSwitch
    ? (availableModels.find(m => m.id === pendingModelSwitch.to)?.label ?? pendingModelSwitch.to)
    : "";

  return (
    <>
    <ModelSwitchConfirmDialog
      open={pendingModelSwitch !== null}
      fromLabel={pendingFromLabel}
      toLabel={pendingToLabel}
      onConfirm={confirmModelSwitch}
      onCancel={cancelModelSwitch}
    />
    <ContinueFreshContext.Provider value={continueFreshValue}>
    <AppShell
      messages={messages}
      messageMetrics={messageMetrics}
      messageOrigin={messageOrigin}
      messageOriginSessionLabel={messageOriginSessionLabel}
      interjections={interjections}
      isLoading={isLoading}
      connected={connected}
      switching={switching}
      hasConnected={hasConnected}
      error={error ?? undefined}
      sessionType={sessionType}
      onSend={handleSend}
      onStop={stop}
      onNewSession={handleNewSession}
      sessions={sessions}
      archivedSessions={archivedSessions}
      activeSessionId={activeSessionId}
      onSwitchSession={switchSession}
      onDeleteSession={deleteSession}
      onRenameSession={renameSession}
      onTogglePin={togglePin}
      onArchiveSession={archiveSession}
      onRestoreSession={restoreSession}
      projects={projects}
      activeProject={projects.find((project) => project.id === activeSession?.project_id) ?? null}
      projectsLoading={projectsLoading}
      projectsError={projectsError}
      onCreateProject={createProject}
      onRenameProject={renameProject}
      onAssignSessionProject={assignSessionProject}
      onProjectPatched={applyProjectUpdate}
      onSessionDocumentSwitched={applySessionDocumentUpdate}
      pendingProjectMoveSessionIds={pendingProjectMoveSessionIds}
      config={config}
      currentModel={displayedModel}
      availableModels={availableModels}
      onModelChange={setModel}
      onEffortChange={setEffort}
      onToggleThinking={toggleThinking}
      voiceState={voice.voiceState}
      getAudioLevel={voice.getAudioLevel}
      onSpeak={voice.speakMessage}
      speakingMessageId={voice.speakingMessageId}
      voiceMode={prefs.voiceMode}
      onToggleVoiceMode={handleToggleVoiceMode}
      onStopSpeaking={voice.stopSpeaking}
      seekTo={voice.seekTo}
      getPlaybackProgress={voice.getPlaybackProgress}
      audioCache={voice.audioCache}
      voiceId={prefs.voiceId}
      onRestartVoice={voice.restartVoice}
      playbackSpeed={voice.playbackSpeed}
      onSpeedChange={handleSpeedChange}
      writingRoomVoice={writingRoomVoice}
      companionName={activeCompanion?.displayName ?? ""}
      companionSubtitle={activeCompanion?.subtitle ?? ""}
      companionShortDesc={activeCompanion?.shortDesc ?? ""}
      companions={serverCompanions}
      prefs={prefs}
      updatePref={updatePref}
      sendReasoningConfig={sendReasoningConfig}
      showReasoning={showReasoning}
      sttAvailable={sttAvailable}
      sendSTTAudio={sendSTTAudio}
      onSTTResultRef={onSTTResultRef}
      sttStreaming={{
        available: sttStreamingAvailable,
        sendStart: sendSTTStreamStart,
        sendStop: sendSTTStreamStop,
        sendBinary: sendBinaryFrame,
        onSegmentRef: onSTTStreamSegmentRef,
        onReadyRef: onSTTStreamReadyRef,
        onDoneRef: onSTTStreamDoneRef,
        onDisconnectRef: onSTTStreamDisconnectRef,
        onErrorRef: onSTTStreamErrorRef,
      }}
      activeAgents={activeAgents}
      sendRawWs={sendRawWs}
      writingRoomDocumentUpdatedRef={onWritingRoomDocumentUpdatedRef}
      roomStateRef={roomStateRef}
      onReactionRef={onReactionRef}
      onReactionSyncRef={onReactionSyncRef}
      pendingReactionsRef={pendingReactionsRef}
      pendingReactionSyncsRef={pendingReactionSyncsRef}
      isReplaying={isReplaying}
      turnPhase={turnPhase}
      turnStartedAt={turnStartedAt}
      subprocessAlive={subprocessAlive}
    />
    </ContinueFreshContext.Provider>
    </>
  );
}
